import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { PermissionsGetPermissionsQuery } from 'generated/resources/graphql'
import { partition, xor } from 'lodash'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import ContextlessPermissionsList from 'modules/permissions/components/ContextlessPermissionsList'

import ConfirmationModal from './ConfirmationModal'
import VerbsWithContext from './VerbsWithContext'
import { ToggleablePermission } from './types'

type Permission = PermissionsGetPermissionsQuery['permissions']['permissions'][number]

interface Props {
  disabled: boolean
  permissions: Permission[]
  permissionGroupId: number
}

function Item({ disabled, permissions, permissionGroupId }: Props) {
  const [modifiedPermissionIds, setModifiedPermissionIds] = useState<string[]>([])
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const toggleablePermissions: ToggleablePermission[] = permissions.map((permission) => {
    return {
      id: permission.id,
      verb: permission.verb,
      verbLabel: permission.verbLabel,
      verbDescription: permission.verbDescription,
      resource: permission.resource,
      resourceTags: permission.resourceTags,
      category: permission.category,
      context: permission.permissionContext,
      contextLess: permission.contextless,
      toggled: modifiedPermissionIds.includes(permission.id)
        ? !permission.authorized
        : permission.authorized,
      toggle: () => {
        // Either add or remove the permission id from the modifiedPermissionIds array
        const updatedModifiedPermissionIds = xor(modifiedPermissionIds, [permission.id])
        setModifiedPermissionIds(updatedModifiedPermissionIds)
      },
    }
  })

  const saveAllFunction = () => {
    setConfirmationModalOpen(true)
  }

  const [contextlessPermissions, contextfulPermissions] = partition(
    toggleablePermissions,
    'contextLess'
  )

  const changes = () =>
    modifiedPermissionIds.map((id) => {
      const permission = permissions.find((p) => p.id === id)
      if (!permission) {
        throw new Error(`Permission with id "${id}" not found`)
      }
      return {
        id,
        verb: permission.verb,
        verbLabel: permission.verbLabel,
        resource: permission.resource ?? null,
        context: permission.permissionContext,
        authorized: !permission.authorized,
        permissionsGroupId: permissionGroupId,
      }
    })

  return (
    <Box gap="s24">
      {contextlessPermissions.length > 0 && (
        <ContextlessPermissionsList permissions={contextlessPermissions} disabled={disabled} />
      )}
      {contextfulPermissions.length > 0 && (
        <VerbsWithContext permissions={contextfulPermissions} disabled={disabled} />
      )}
      {modifiedPermissionIds.length > 0 && (
        <Box padding="s16" alignItems="flexEnd">
          <Button onClick={saveAllFunction}>{i18n.t('common.save_changes')}</Button>
        </Box>
      )}
      {confirmationModalOpen && (
        <ConfirmationModal
          onClose={() => {
            setConfirmationModalOpen(false)
            setModifiedPermissionIds([])
          }}
          permissionGroupId={permissionGroupId}
          changes={changes()}
        />
      )}
    </Box>
  )
}

export default observer(Item)
